import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpResponse
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, from, of } from 'rxjs';
import { catchError, filter, take, switchMap, finalize, tap, concatMap } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import { environment } from 'src/environments/environment';
import { LoadingService } from 'src/app/Services/loading.service';
import { CentralizedLoadingService } from 'src/app/Services/centralizedLoading.service';

const triplinkConfig = environment.tripLinkConfig;

@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private tokenRefreshPromise: Promise<any> | null = null;
  private scopes = [triplinkConfig.consentScopes[0], 'offline_access'] // Replace with your required scopes
  private isLoggingOut = false;

  private skipLoadingUrls = [
    'Genset/getgensetlocationinfo',
    'ui/getdashboardlocationinfogeofence',
    'devicefwinfogeofencesummary',
    'ui/alarminfogeofencesummary',
    'ui/assetmodelinfogeofencesummary',
    'ui/controllerfwinfogeofencesummary',
    'ui/setpointinfogeofencesummary',
    'ui/assetmanufacturerinfogeofencesummary?companyID',
    'Genset/getreportingstatussummary',
    'ui/assetpowerstatusgeofencesummary',
    'ui/assetnotreportingstatusgeofencesummary',
    'ui/tripwiseinfogeofencesummary',
    'ui/tripwiseexpirysummarygeofence',
    'filter/getfilter?companyId',
    'ui/containerinfo?containerId=',
    'Genset/getgensetlocationdetails?gensetId=',
    'gridsettings/get',
    // 'geofence/getall?companyID=',
    'leet/getfleetswithuserandassetCount?companyID=',
    'filter/getfilter?companyId=',
    'filter/getalarms',
  ];
  constructor(
    private authService: MsalService,
    private loadingService: LoadingService,
    private centralizedLoadingService: CentralizedLoadingService,
  ) { }

  private shouldSkipLoading(url: string): boolean {
    return this.skipLoadingUrls.some(skipUrl => url.toLowerCase().includes(skipUrl.toLowerCase()));
  }


  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    // Check if this request should skip loading
    const skipLoading = this.shouldSkipLoading(request.url);

    // Generate unique ID for this request only if not skipping loading
    const requestId = skipLoading ? null : this.centralizedLoadingService.startLoading();
    // Add request ID to headers for tracking (optional)
    const modifiedReq = request.clone({
      setHeaders: {
        'X-Request-ID': requestId || 'no-loading',
        'Connection': 'keep-alive'
      }
    });


    // Generate unique ID for this request
    // const requestId = this.centralizedLoadingService.startLoading();

    // // Add request ID to headers for tracking (optional)
    // const modifiedReq = request.clone({
    //   setHeaders: {
    //     'X-Request-ID': requestId
    //   }
    // });
    // First, check if we need to add a token to this request
    return this.addTokenToCurrentRequest(modifiedReq).pipe(
      concatMap(requestWithToken => {
        // Process the request with the token
        return next.handle(requestWithToken).pipe(
          catchError(error => {
            if (error instanceof HttpErrorResponse) {
              // Handle 401 Unauthorized errors (token expired)
              // if (error.status === 401 && !error.url?.toLowerCase().includes('company/getall')) {
              //   return this.handle401Error(modifiedReq, next, requestId, skipLoading);
              // }
            }
            return throwError(error);
          }),
          tap(
            (event: HttpEvent<any>) => {
              if (event instanceof HttpResponse) {
                // Request completed successfully
                // console.log(`Request ${requestId} completed successfully`);
              }
            },
            (error: HttpErrorResponse) => {
              // Request failed
              // console.error(`Request ${requestId} failed:`, error);
            }
          ),
          finalize(() => {
            // Always stop loading for this request when it completes (success or error)
            if (!skipLoading && requestId) {
              this.centralizedLoadingService.stopLoading(requestId);
            }
            // console.log(`Request ${requestId} finalized. Active requests: ${this.centralizedLoadingService.getActiveRequestsCount()}`);
          })
        );
      })
    );
  }

  private addTokenToCurrentRequest(request: HttpRequest<any>): Observable<HttpRequest<any>> {
    // If we're already refreshing the token, wait for it to complete
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter(result => result !== null),
        take(1),
        switchMap(tokenResponse => {
          if (tokenResponse) {
            return of(this.addTokenToRequest(request, tokenResponse.accessToken));
          }
          return of(request);
        })
      );
    }

    // Otherwise, get the current token
    const account = this.authService.instance.getActiveAccount();
    if (!account) {
      return of(request);
    }

    // Check if token is in cache and not expired
    return from(this.authService.instance.acquireTokenSilent({
      account: account,
      scopes: this.scopes,
      forceRefresh: false
    }).then(tokenResponse => {
      return this.addTokenToRequest(request, tokenResponse.accessToken);
    }).catch(() => {
      // If silent acquisition fails, return the original request
      return request;
    }));
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler, requestId?: any, skipLoading?:any): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      // Show loading indicator
      this.loadingService.show();

      const account = this.authService.instance.getActiveAccount();
      // if (!account) {
      //   this.isRefreshing = false;
      //   this.loadingService.hide();
      //   this.authService.loginRedirect();
      //   return throwError('User not logged in');
      // }

      // Create a single promise for token refresh that all requests can wait on
      this.tokenRefreshPromise = this.authService.instance.acquireTokenSilent({
        account: account,
        scopes: this.scopes,
        forceRefresh: true
      });
      // .catch(error => {
      //   console.error('Token refresh failed:', error);
      //   this.loadingService.hide();

      //   if (error.errorCode === 'consent_required' || 
      //       error.errorCode === 'interaction_required' || 
      //       error.errorCode === 'login_required') {
      //     this.authService.loginRedirect();
      //   }
      //   return null;
      // });

      return from(this.tokenRefreshPromise).pipe(
        tap(tokenResponse => {
          this.isRefreshing = false;
          if (tokenResponse) {
            this.refreshTokenSubject.next(tokenResponse);
          } else {
            this.refreshTokenSubject.next(false);
          }
          this.loadingService.hide();
        }),
        switchMap(tokenResponse => {
          if (tokenResponse) {
            // Retry the request with the new token
            return next.handle(this.addTokenToRequest(request, tokenResponse.accessToken));
          }
          return throwError('Failed to refresh token');
        }),
        catchError(error => {
          this.isRefreshing = false;
          this.loadingService.hide();
          if (!this.isLoggingOut) {
            this.isLoggingOut = true;
            // Handle refresh token expiration
            if (confirm('Your session has expired! Please login to continue.')) {
              // Clear any stored session data
              sessionStorage.clear();
              localStorage.clear();

              // Log the user out and redirect to login page
              this.authService.logout();
            }
            setTimeout(() => {
              this.isLoggingOut = false;
            }, 1000);

          }

          return throwError(error);
        }),
        finalize(() => {
          this.isRefreshing = false;
          this.tokenRefreshPromise = null;
          this.loadingService.hide();
          if (!skipLoading && requestId) {
              this.centralizedLoadingService.stopLoading(requestId);
            }

        })
      );
    } else {
      // If a token refresh is already in progress, wait for it to complete
      // and then retry the request with the new token
      return this.refreshTokenSubject.pipe(
        filter(result => result !== null),
        take(1),
        switchMap(tokenResponse => {
          if (tokenResponse) {
            return next.handle(this.addTokenToRequest(request, tokenResponse.accessToken));
          }
          return throwError('Failed to refresh token');
        })
      );
    }
  }

  private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }
}
